import {useState} from 'react';
import {
  Plus,
  Pencil,
  Save,
  X,
  Trash2,
  ChevronDown,
  ChevronRight,
  Shield,
  AlertCircle,
  MailWarning,
  EyeOff,
  ImageOff
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Switch
} from '@kroll-resolver-crisp/ui-components-react';
import {getApiErrorMessage} from '@/api/client';
import {useConfirm} from '@/components/shared/useConfirm';
import {
  type ClassificationConfig,
  type CreateClassificationConfigRequest,
  type UpdateClassificationConfigRequest
} from '@/api/classificationConfigs';
import {isPolicyViolationType} from '@/lib/classificationType';

type Props = {
  readonly item: ClassificationConfig;
  readonly allConfigs: ClassificationConfig[];
  readonly level: number;
  // Active taxonomy tab ('Actor' or 'Content'). Verticals are shared; risks are scoped to this.
  readonly category: 'Actor' | 'Content';
  readonly onUpdate: (id: string, data: UpdateClassificationConfigRequest) => Promise<void>;
  readonly onCreate: (data: CreateClassificationConfigRequest) => Promise<ClassificationConfig>;
  readonly onDelete: (id: string) => Promise<void>;
  readonly onToggleHidden: (id: string, hidden: boolean) => Promise<void>;
};

export function ClassificationTreeItem({
  item,
  allConfigs,
  level,
  category,
  onUpdate,
  onCreate,
  onDelete,
  onToggleHidden
}: Props) {
  const [expanded, setExpanded] = useState(level === 0); // Auto-expand root level
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editData, setEditData] = useState({
    name: item.name,
    display_name: item.display_name,
    restricted: item.restricted,
    is_potential_p0: item.is_potential_p0,
    purge_imagery: item.purge_imagery,
    sort_order: item.sort_order
  });
  const [newItemData, setNewItemData] = useState({
    name: '',
    display_name: '',
    sort_order: (allConfigs.filter((c) => c.parent_id === item.id).length ?? 0) + 1
  });
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {confirm, confirmDialog} = useConfirm();

  // Direct children of this item. Under a policy violation they are scoped to the active
  // taxonomy, except that a child carrying no category at all belongs to both rather than to
  // neither - the same rule the classify picker applies.
  const children = allConfigs
    .filter((c) => c.parent_id === item.id && c.categories.includes(category))
    .sort((a, b) => a.sort_order - b.sort_order);

  const isRoot = level === 0;
  const hasChildren = children.length > 0;

  const handleSaveEdit = async () => {
    if (
      editData.purge_imagery &&
      !item.purge_imagery &&
      !(await confirm({
        title: `Purge imagery for "${item.display_name}"?`,
        description:
          'Every image stored for an item classified under this entry, or anything beneath it, will be deleted. This includes items already classified, and it cannot be undone. Submissions and classifications are kept.',
        confirmLabel: 'Purge imagery'
      }))
    ) {
      return;
    }

    setError(null);
    try {
      await onUpdate(item.id, {
        name: editData.name.trim(),
        display_name: editData.display_name.trim(),
        ...(isRoot
          ? {restricted: editData.restricted}
          : {is_potential_p0: editData.is_potential_p0}),
        purge_imagery: editData.purge_imagery,
        sort_order: editData.sort_order
      });
      setEditing(false);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to update.'));
    }
  };

  const handleCreate = async () => {
    if (!newItemData.name.trim() || !newItemData.display_name.trim()) {
      return;
    }

    setError(null);
    try {
      await onCreate({
        name: newItemData.name.trim(),
        display_name: newItemData.display_name.trim(),
        parent_id: item.id,
        category: isPolicyViolationType(item.type) ? category : null,
        sort_order: newItemData.sort_order
      });
      setNewItemData({name: '', display_name: '', sort_order: children.length + 1});
      setCreating(false);
      setExpanded(true);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to create.'));
    }
  };

  const handleToggleHidden = async () => {
    setError(null);
    try {
      await onToggleHidden(item.id, !item.hidden_facets.includes(category));
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to update visibility.'));
    }
  };

  const handleDelete = async () => {
    if (
      !(await confirm({
        title: `Delete "${item.display_name}"${hasChildren ? ` and all ${children.length} children` : ''}?`
      }))
    ) {
      return;
    }

    setError(null);
    try {
      setDeleting(true);
      await onDelete(item.id);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to delete.'));
      setDeleting(false);
    }
  };

  const isHidden = item.hidden_facets.includes(category);

  return (
    <div className="border-b last:border-b-0">
      {/* Item Row */}
      <div
        className="hover:bg-muted/50 flex items-center gap-2 py-1.5 pr-3"
        style={{paddingLeft: `${level * 1.5 + 0.5}rem`}}>
        {/* Expand/Collapse */}
        {hasChildren ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${item.display_name}` : `Expand ${item.display_name}`}
            onClick={() => {
              setExpanded(!expanded);
            }}>
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </Button>
        ) : (
          <div className="size-7 shrink-0" />
        )}

        {/* Name & Display Name */}
        {editing ? (
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <Input
              type="text"
              value={editData.name}
              className="min-w-40 flex-1"
              placeholder="Name"
              aria-label="Name"
              onChange={(e) => {
                setEditData({...editData, name: e.target.value});
              }}
            />
            <Input
              type="text"
              value={editData.display_name}
              className="min-w-40 flex-1"
              placeholder="Display Name"
              aria-label="Display Name"
              onChange={(e) => {
                setEditData({...editData, display_name: e.target.value});
              }}
            />
            {isRoot ? (
              <Label>
                <Checkbox
                  checked={editData.restricted}
                  onCheckedChange={(checked) => {
                    setEditData({...editData, restricted: checked});
                  }}
                />
                Restricted
              </Label>
            ) : (
              <Label>
                <Checkbox
                  checked={editData.is_potential_p0}
                  onCheckedChange={(checked) => {
                    setEditData({...editData, is_potential_p0: checked});
                  }}
                />
                Potential P0
              </Label>
            )}
            <Label>
              <Checkbox
                checked={editData.purge_imagery}
                onCheckedChange={(checked) => {
                  setEditData({...editData, purge_imagery: checked});
                }}
              />
              Purge imagery
            </Label>
            <Button variant="ghost" size="icon-sm" aria-label="Save" onClick={handleSaveEdit}>
              <Save />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Cancel"
              onClick={() => {
                setEditing(false);
              }}>
              <X />
            </Button>
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={isRoot ? 'font-medium' : undefined}>{item.display_name}</span>
                {item.name === item.display_name ? null : (
                  <span className="text-muted-foreground">({item.name})</span>
                )}
                {item.restricted ? (
                  <Tooltip>
                    <TooltipTrigger render={<span className="inline-flex" />}>
                      <Shield className="text-destructive-ink size-3" aria-label="Restricted" />
                    </TooltipTrigger>
                    <TooltipContent>Restricted</TooltipContent>
                  </Tooltip>
                ) : null}
                {item.is_potential_p0 ? (
                  <Tooltip>
                    <TooltipTrigger render={<span className="inline-flex" />}>
                      <MailWarning
                        className="text-destructive-ink size-3"
                        aria-label="Potential P0"
                      />
                    </TooltipTrigger>
                    <TooltipContent>Potential P0</TooltipContent>
                  </Tooltip>
                ) : null}
                {item.purge_imagery ? (
                  <Tooltip>
                    <TooltipTrigger render={<span className="inline-flex" />}>
                      <ImageOff
                        className="text-destructive-ink size-3"
                        aria-label="Purge imagery"
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      Purge imagery: stored images are deleted once classified
                    </TooltipContent>
                  </Tooltip>
                ) : null}
                {isHidden ? (
                  <Tooltip>
                    <TooltipTrigger render={<span className="inline-flex" />}>
                      <Badge variant="secondary">
                        <EyeOff />
                        Hidden
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>Hidden from analysts</TooltipContent>
                  </Tooltip>
                ) : null}
              </div>
              {item.matches?.length ? (
                <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                  <span>maps to</span>
                  {item.matches.map((match) => (
                    <Badge key={match.concept_id} variant="outline">
                      {match.name}
                      <span className="text-muted-foreground"> · {match.kind}</span>
                    </Badge>
                  ))}
                </div>
              ) : null}
            </div>
            <span className="text-muted-foreground font-mono text-xs">#{item.sort_order}</span>
            {hasChildren ? (
              <span className="text-muted-foreground text-xs">{children.length} items</span>
            ) : null}

            {/* Actions */}
            <Tooltip>
              <TooltipTrigger render={<span className="inline-flex items-center" />}>
                <Switch
                  size="sm"
                  checked={!isHidden}
                  aria-label={
                    isHidden
                      ? `Show ${item.display_name} to analysts`
                      : `Hide ${item.display_name} from analysts`
                  }
                  onCheckedChange={handleToggleHidden}
                />
              </TooltipTrigger>
              <TooltipContent>
                {isHidden ? 'Hidden from analysts' : 'Visible to analysts'}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Add child to ${item.display_name}`}
                    onClick={() => {
                      setCreating(!creating);
                    }}
                  />
                }>
                <Plus />
              </TooltipTrigger>
              <TooltipContent>Add child</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Edit ${item.display_name}`}
                    onClick={() => {
                      setEditing(true);
                    }}
                  />
                }>
                <Pencil />
              </TooltipTrigger>
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={deleting}
                    className="hover:text-destructive-ink"
                    aria-label={`Delete ${item.display_name}`}
                    onClick={handleDelete}
                  />
                }>
                <Trash2 />
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>

      {error ? (
        <div className="px-3 pb-2" style={{paddingLeft: `${level * 1.5 + 0.5}rem`}}>
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      ) : null}

      {/* Create Child Row */}
      {creating ? (
        <div
          className="flex items-center gap-2 border-t py-2 pr-3"
          style={{paddingLeft: `${(level + 1) * 1.5 + 0.5}rem`}}>
          <div className="size-7 shrink-0" />
          <Input
            type="text"
            value={newItemData.name}
            className="flex-1"
            placeholder="Name"
            aria-label="New name"
            onChange={(e) => {
              setNewItemData({...newItemData, name: e.target.value});
            }}
          />
          <Input
            type="text"
            value={newItemData.display_name}
            className="flex-1"
            placeholder="Display Name"
            aria-label="New display name"
            onChange={(e) => {
              setNewItemData({...newItemData, display_name: e.target.value});
            }}
          />
          <Button variant="ghost" size="icon-sm" aria-label="Save new child" onClick={handleCreate}>
            <Save />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Cancel new child"
            onClick={() => {
              setCreating(false);
            }}>
            <X />
          </Button>
        </div>
      ) : null}

      {/* Recursive Children */}
      {expanded && hasChildren ? (
        <div>
          {children.map((child) => (
            <ClassificationTreeItem
              key={child.id}
              item={child}
              allConfigs={allConfigs}
              level={level + 1}
              category={category}
              onUpdate={onUpdate}
              onCreate={onCreate}
              onDelete={onDelete}
              onToggleHidden={onToggleHidden}
            />
          ))}
        </div>
      ) : null}
      {confirmDialog}
    </div>
  );
}
